import {
    type DocumentBankDetails,
    getBrandLogoUrl,
    getBrandSignatureUrl,
    getOrgConfig,
    updateBrandLetterhead,
    uploadBrandLogo,
    uploadBrandSignature,
} from '@api/organization';
import { getOrganizationIdFromStorage } from '@components/containers/procurementOrders/formComponents';
import useToaster from '@hooks/toaster/useToaster';
import { Button, FileButton, Group, Select, Text, Textarea, TextInput } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FC } from 'react';

import { GST_STATE_OPTIONS } from '@diagon-alley/common';

import classes from './BrandLetterheadTab.module.css';

/**
 * What every document this brand issues says about who issued it.
 *
 * IT EXISTS BECAUSE THE DOCUMENTS COULD NOT SAY IT. Until now an organisation carried a name and
 * nothing else, so a purchase or production order could print the brand's name and then had to
 * ask somebody to type its own address into the Bill To block, on every single order. This is
 * that information, entered once.
 *
 * THE PREVIEW IS THE POINT OF THE SCREEN. These fields are only ever seen as a letterhead, and a
 * form of six inputs gives no sense of how they will read once stacked. So the block is shown as
 * it will print, next to the fields that change it — the same reasoning as the order authoring
 * screen, at a smaller scale.
 */
const BrandLetterheadTab: FC = () => {
    const { showToastSuccess, showToastError } = useToaster();
    const queryClient = useQueryClient();
    const organizationId = useMemo(() => getOrganizationIdFromStorage(), []);

    const [legalName, setLegalName] = useState('');
    const [email, setEmail] = useState('');
    const [gstin, setGstin] = useState('');
    const [cin, setCin] = useState('');
    const [address, setAddress] = useState('');
    const [phone, setPhone] = useState('');
    const [stateCode, setStateCode] = useState<string | null>(null);
    const [website, setWebsite] = useState('');
    const [terms, setTerms] = useState('');
    const [signatoryName, setSignatoryName] = useState('');
    const [signatoryDesignation, setSignatoryDesignation] = useState('');
    /** Printed on B2B proforma invoices so the customer knows where to pay. */
    const [bank, setBank] = useState<DocumentBankDetails>({});
    const [dirty, setDirty] = useState(false);

    const orgQuery = useQuery({
        queryKey: ['org-config', organizationId],
        queryFn: () => getOrgConfig(organizationId as string),
        enabled: !!organizationId,
    });

    /**
     * The logo comes back as a signed link rather than inlined bytes.
     *
     * A screen can survive a missing image; the PDF cannot, which is why the renderer inlines it
     * instead. Two mechanisms for one asset, on purpose.
     */
    const logoQuery = useQuery({
        queryKey: ['brand-logo-url'],
        queryFn: getBrandLogoUrl,
        enabled: !!organizationId,
    });

    // Seed once the record arrives, and never again — otherwise a refetch mid-edit would
    // overwrite what somebody is typing.
    useEffect(() => {
        const org = orgQuery.data;
        if (!org || dirty) return;
        setLegalName(org.legalName ?? '');
        setEmail(org.email ?? '');
        setGstin(org.gstin ?? '');
        setCin(org.cin ?? '');
        setAddress(org.address ?? '');
        setPhone(org.phone ?? '');
        setStateCode(org.stateCode ?? null);
        setWebsite(org.website ?? '');
        setTerms(org.documentTerms ?? '');
        setSignatoryName(org.signatoryName ?? '');
        setSignatoryDesignation(org.signatoryDesignation ?? '');
        setBank(org.bankDetails ?? {});
    }, [orgQuery.data, dirty]);

    const save = useMutation({
        mutationFn: () =>
            updateBrandLetterhead({
                // Trimmed, and an emptied field is sent as null rather than '' — the document
                // asks "is there an address" and an empty string answers yes.
                legalName: legalName.trim() || null,
                email: email.trim() || null,
                gstin: gstin.trim() || null,
                cin: cin.trim() || null,
                address: address.trim() || null,
                phone: phone.trim() || null,
                stateCode: stateCode || null,
                website: website.trim() || null,
                termsAndConditions: terms.trim() || null,
                signatoryName: signatoryName.trim() || null,
                signatoryDesignation: signatoryDesignation.trim() || null,
                bankDetails: Object.values(bank).some((v) => v && String(v).trim())
                    ? Object.fromEntries(Object.entries(bank).map(([k, v]) => [k, v && String(v).trim() ? String(v).trim() : null]))
                    : null,
            }),
        onSuccess: async () => {
            setDirty(false);
            await queryClient.invalidateQueries({ queryKey: ['org-config'] });
            showToastSuccess({ title: 'Letterhead saved' });
        },
        onError: (err: any) =>
            showToastError({
                title: 'Could not save the letterhead',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            }),
    });

    const signatureQuery = useQuery({
        queryKey: ['brand-signature-url'],
        queryFn: getBrandSignatureUrl,
        enabled: !!organizationId,
    });

    const upload = useMutation({
        mutationFn: (file: File) => uploadBrandLogo(file),
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['brand-logo-url'] }),
                queryClient.invalidateQueries({ queryKey: ['org-config'] }),
            ]);
            showToastSuccess({ title: 'Logo updated' });
        },
        onError: (err: any) =>
            showToastError({
                title: 'Could not upload the logo',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            }),
    });

    const uploadSignature = useMutation({
        mutationFn: (file: File) => uploadBrandSignature(file),
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['brand-signature-url'] }),
                queryClient.invalidateQueries({ queryKey: ['org-config'] }),
            ]);
            showToastSuccess({ title: 'Signature updated' });
        },
        onError: (err: any) =>
            showToastError({
                title: 'Could not upload the signature',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            }),
    });

    const editBank = (patch: Partial<DocumentBankDetails>) => {
        setDirty(true);
        setBank((b) => ({ ...b, ...patch }));
    };

    const edit = (setter: (value: string) => void) => (value: string) => {
        setDirty(true);
        setter(value);
    };

    const stateLabel = GST_STATE_OPTIONS.find((s) => s.value === stateCode)?.label ?? '';

    return (
        <div className={classes.root}>
            <div>
                <Text fw={600} size="sm" className="text-slate-900">
                    Brand letterhead
                </Text>
                <Text size="xs" c="dimmed" mt={2}>
                    Printed at the top of every purchase order, production order and proforma invoice you issue.
                </Text>
            </div>

            <div className={classes.split}>
                <div className={classes.form}>
                    <div>
                        <Text size="xs" fw={500} mb={4}>
                            Logo
                        </Text>
                        <Group gap={12} align="center">
                            {logoQuery.data?.url ? (
                                <img
                                    src={logoQuery.data.url}
                                    alt="Brand logo"
                                    className={classes.logo}
                                />
                            ) : (
                                <div className={classes.logoEmpty}>No logo</div>
                            )}
                            <div>
                                <FileButton
                                    onChange={(file) => file && upload.mutate(file)}
                                    accept="image/png,image/jpeg"
                                >
                                    {(props) => (
                                        <Button
                                            {...props}
                                            size="xs"
                                            variant={ButtonVariant.DEFAULT}
                                            loading={upload.isPending}
                                        >
                                            {logoQuery.data?.url ? 'Replace' : 'Upload'}
                                        </Button>
                                    )}
                                </FileButton>
                                <Text size="10px" c="dimmed" mt={4}>
                                    PNG or JPEG, up to 512KB
                                </Text>
                            </div>
                        </Group>
                    </div>

                    <TextInput
                        label="Registered legal name"
                        description="The entity your GSTIN belongs to. Printed at the top of your documents and in Bill To; the short brand name stays on screen."
                        placeholder="ACME FOODS PRIVATE LIMITED"
                        value={legalName}
                        onChange={(e) => edit(setLegalName)(e.currentTarget.value)}
                    />
                    <TextInput
                        label="GSTIN"
                        placeholder="29AAACN1234F1Z5"
                        value={gstin}
                        onChange={(e) => edit(setGstin)(e.currentTarget.value)}
                    />
                    <TextInput
                        label="CIN"
                        description="Corporate Identity Number, for companies. Leave blank if you don't have one."
                        placeholder="U52100KA2019PTC127993"
                        value={cin}
                        onChange={(e) => edit(setCin)(e.currentTarget.value.toUpperCase())}
                    />
                    <Textarea
                        label="Registered address"
                        placeholder={'Prestige Tech Park\nBengaluru 560103'}
                        value={address}
                        onChange={(e) => edit(setAddress)(e.currentTarget.value)}
                        autosize
                        minRows={3}
                    />
                    <Group grow>
                        <TextInput
                            label="Phone"
                            placeholder="+91 98765 43210"
                            value={phone}
                            onChange={(e) => edit(setPhone)(e.currentTarget.value)}
                        />
                        <TextInput
                            label="Billing email"
                            placeholder="accounts@yourbrand.com"
                            value={email}
                            onChange={(e) => edit(setEmail)(e.currentTarget.value)}
                        />
                        <Select
                            label="State"
                            placeholder="Pick your GST state"
                            data={GST_STATE_OPTIONS}
                            value={stateCode}
                            onChange={(v) => {
                                setDirty(true);
                                setStateCode(v);
                            }}
                            searchable
                            clearable
                        />
                    </Group>
                    <TextInput
                        label="Website"
                        placeholder="https://example.com"
                        value={website}
                        onChange={(e) => edit(setWebsite)(e.currentTarget.value)}
                    />

                    {/*
                     * The terms print at the foot of the document rather than the head, so they
                     * sit apart from the letterhead fields above and say what they seed.
                     */}
                    <Textarea
                        label="Standard terms and conditions"
                        description="Fills in when you start a purchase or production order. You can still change them on any single order."
                        placeholder={
                            '1. The product shall be accepted only if it complies with the '
                            + 'approved specifications.\n'
                            + '2. The manufacturer shall be solely responsible for the quality, '
                            + 'safety and regulatory compliance of the supplied product.'
                        }
                        value={terms}
                        onChange={(e) => edit(setTerms)(e.currentTarget.value)}
                        autosize
                        minRows={4}
                    />

                    {/*
                      * The signature sits with the terms rather than the logo: both print at the
                      * foot of the document, and both are about what it claims rather than who
                      * sent it.
                      */}
                    <div>
                        <Text size="xs" fw={500} mb={4}>
                            Signature
                        </Text>
                        <Group gap={12} align="center">
                            {signatureQuery.data?.url ? (
                                <img
                                    src={signatureQuery.data.url}
                                    alt="Authorised signature"
                                    className={classes.signature}
                                />
                            ) : (
                                <div className={classes.logoEmpty}>No signature</div>
                            )}
                            <div>
                                <FileButton
                                    onChange={(file) => file && uploadSignature.mutate(file)}
                                    accept="image/png,image/jpeg"
                                >
                                    {(props) => (
                                        <Button
                                            {...props}
                                            size="xs"
                                            variant={ButtonVariant.DEFAULT}
                                            loading={uploadSignature.isPending}
                                        >
                                            {signatureQuery.data?.url ? 'Replace' : 'Upload'}
                                        </Button>
                                    )}
                                </FileButton>
                                {/*
                                  * The transparency note is the whole point of this line. A JPEG
                                  * signature carries its white paper with it and prints inside a
                                  * visible grey box on the document; a PNG with a transparent
                                  * background sits on the page. The preview alongside shows it
                                  * on white, so the box is obvious before anything is sent.
                                  */}
                                <Text size="10px" c="dimmed" mt={4}>
                                    PNG with a transparent background, cropped close. JPEG works
                                    but prints inside a white box.
                                </Text>
                            </div>
                        </Group>
                    </div>

                    <Group grow>
                        <TextInput
                            label="Signatory name"
                            placeholder="Aryendra Sharma"
                            value={signatoryName}
                            onChange={(e) => edit(setSignatoryName)(e.currentTarget.value)}
                        />
                        <TextInput
                            label="Designation"
                            placeholder="Authorised Signatory"
                            value={signatoryDesignation}
                            onChange={(e) => edit(setSignatoryDesignation)(e.currentTarget.value)}
                        />
                    </Group>

                    <Text size="sm" fw={600} mt={8}>
                        Bank details (printed on B2B proforma invoices)
                    </Text>
                    <Group grow>
                        <TextInput label="Account name" value={bank.accountName ?? ''} onChange={(e) => editBank({ accountName: e.currentTarget.value })} />
                        <TextInput label="Account number" value={bank.accountNumber ?? ''} onChange={(e) => editBank({ accountNumber: e.currentTarget.value })} />
                    </Group>
                    <Group grow>
                        <TextInput label="IFSC" value={bank.ifsc ?? ''} onChange={(e) => editBank({ ifsc: e.currentTarget.value.toUpperCase() })} />
                        <TextInput label="Bank" value={bank.bankName ?? ''} onChange={(e) => editBank({ bankName: e.currentTarget.value })} />
                    </Group>
                    <Group grow>
                        <TextInput label="Branch" value={bank.branch ?? ''} onChange={(e) => editBank({ branch: e.currentTarget.value })} />
                        <TextInput label="UPI ID" value={bank.upiId ?? ''} onChange={(e) => editBank({ upiId: e.currentTarget.value })} />
                    </Group>

                    <Group justify="flex-end" mt={4}>
                        <Button
                            onClick={() => save.mutate()}
                            loading={save.isPending}
                            disabled={!dirty}
                        >
                            Save
                        </Button>
                    </Group>
                </div>

                {/* As it will print. */}
                <div className={classes.previewPane}>
                    <Text size="10px" fw={600} className={classes.previewLabel}>
                        How it will read
                    </Text>
                    <div className={classes.sheet}>
                        {logoQuery.data?.url && (
                            <img
                                src={logoQuery.data.url}
                                alt=""
                                className={classes.sheetLogo}
                            />
                        )}
                        <div className={classes.sheetName}>{legalName.trim() || orgQuery.data?.name || '—'}</div>
                        {gstin && <div className={classes.sheetLine}>GSTIN: {gstin}</div>}
                        {cin && <div className={classes.sheetLine}>CIN: {cin}</div>}
                        {address && <div className={classes.sheetLine}>{address}</div>}
                        {stateLabel && <div className={classes.sheetLine}>{stateLabel}</div>}
                        {phone && <div className={classes.sheetLine}>Mobile: {phone}</div>}
                        {email && <div className={classes.sheetLine}>Email: {email}</div>}
                        {website && <div className={classes.sheetLine}>Website: {website}</div>}
                        {!gstin && !cin && !address && !phone && !email && !website && (
                            <div className={classes.sheetEmpty}>
                                Nothing filled in yet — documents will show the brand name alone.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BrandLetterheadTab;
